import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import {
  Sparkles, Wand2, CalendarClock, X, Loader2, Check
} from 'lucide-react';

// Caption styles supported by /ai/generate-caption(s).
const STYLES = [
  { key: 'minimalistic', label: 'Minimal',   hint: 'Short, clean, professional' },
  { key: 'storytelling', label: 'Story',     hint: 'Narrative + lifestyle hook' },
  { key: 'lifestyle',    label: 'Lifestyle', hint: 'Moments + emotion' },
  { key: 'witty',        label: 'Witty',     hint: 'Playful, attention-grabbing' },
];

// Default scheduling = next round hour ≥ now + 1h.
const defaultScheduleDt = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  return d;
};
const fmtDateInput = (d) => d.toISOString().slice(0, 10);
const fmtTimeInput = (d) => d.toTimeString().slice(0, 5);

// Inline schedule + caption bottom-sheet.
// Props:
//   open, onOpenChange : drawer state
//   caption, setCaption: parent-controlled caption text
//   postType           : "new_listing" | "under_contract" | ...
//   propertyDetails    : { beds, baths, sqft, address, price, description }
//   onSchedule(scheduledIso, caption) : called when user taps "Schedule post"
//   loading            : busy state from parent
export const ScheduleSheet = ({
  open, onOpenChange,
  caption, setCaption,
  postType = 'new_listing',
  propertyDetails = {},
  onSchedule,
  loading: parentLoading,
}) => {
  const initialDt = useMemo(() => defaultScheduleDt(), []);
  const [date, setDate] = useState(() => fmtDateInput(initialDt));
  const [time, setTime] = useState(() => fmtTimeInput(initialDt));
  const [style, setStyle] = useState('minimalistic');
  const [generating, setGenerating] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]); // [{style, caption}]
  const [loadingBatch, setLoadingBatch] = useState(false);

  // Reset batch options when sheet re-opens
  useEffect(() => {
    if (!open) {
      setBatchOptions([]);
    }
  }, [open]);

  const buildCaptionRequest = (chosenStyle) => ({
    property_details: propertyDetails,
    post_type: postType,
    caption_style: chosenStyle,
  });

  const generateSingle = async () => {
    setGenerating(true);
    try {
      const res = await apiClient.post('/ai/generate-caption', buildCaptionRequest(style));
      setCaption(res.data.caption);
      toast.success('Caption generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate caption');
    } finally {
      setGenerating(false);
    }
  };

  const generateBatch = async () => {
    setLoadingBatch(true);
    try {
      const res = await apiClient.post('/ai/generate-captions', buildCaptionRequest(style));
      const captions = (res.data.captions || []).filter(c => c.caption);
      setBatchOptions(captions);
      if (captions.length === 0) {
        toast.info('No variations could be generated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate variations');
    } finally {
      setLoadingBatch(false);
    }
  };

  const pickBatchOption = (opt) => {
    setCaption(opt.caption);
    setStyle(opt.style);
    setBatchOptions([]);
    toast.success(`Using ${STYLES.find(s => s.key === opt.style)?.label || opt.style} caption`);
  };

  const handleSchedule = () => {
    if (!date || !time) {
      toast.error('Pick a date and time');
      return;
    }
    const scheduledFor = new Date(`${date}T${time}`);
    if (Number.isNaN(scheduledFor.getTime())) {
      toast.error('Invalid date/time');
      return;
    }
    if (scheduledFor.getTime() < Date.now() + 60_000) {
      toast.error('Schedule must be at least 1 minute in the future');
      return;
    }
    onSchedule(scheduledFor.toISOString(), caption);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="bg-white border-0 max-h-[92vh]"
        data-testid="schedule-sheet"
      >
        <DrawerTitle className="sr-only">Schedule post</DrawerTitle>

        {/* Header */}
        <div className="px-6 pt-2 pb-1 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mt-2">Schedule</p>
            <h2
              className="mt-1 text-3xl font-light leading-[1.05] tracking-tight text-black"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Schedule <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>post.</span>
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="h-9 w-9 mt-2 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            data-testid="schedule-sheet-close"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="px-6 pb-8 overflow-y-auto space-y-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {/* Caption section */}
          <section>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3">Caption</p>

            {batchOptions.length === 0 ? (
              <>
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={4}
                  placeholder="Write a caption or generate one with AI…"
                  className="text-sm rounded-2xl border-gray-200 focus-visible:ring-black/10 focus-visible:border-black/40"
                  data-testid="schedule-sheet-caption-input"
                />

                {/* Style chips */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {STYLES.map(s => {
                    const active = s.key === style;
                    return (
                      <button
                        key={s.key}
                        onClick={() => setStyle(s.key)}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide border transition-colors ${
                          active
                            ? 'bg-[#0B0C10] border-[#0B0C10] text-white'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                        }`}
                        data-testid={`schedule-sheet-style-${s.key}`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* Generate actions */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={generateSingle}
                    disabled={generating || loadingBatch}
                    className="flex-1 h-11 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0B0C10] text-white text-sm font-medium hover:bg-gray-900 active:scale-[0.98] disabled:opacity-60 transition-all"
                    data-testid="schedule-sheet-generate-single"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {generating ? 'Generating…' : 'Generate with AI'}
                  </button>
                  <button
                    onClick={generateBatch}
                    disabled={generating || loadingBatch}
                    className="h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-700 hover:border-gray-400 active:scale-[0.98] disabled:opacity-60 transition-all"
                    data-testid="schedule-sheet-generate-batch"
                  >
                    {loadingBatch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    Try 4 styles
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">Tap a variation to use it.</p>
                <div className="space-y-2">
                  {batchOptions.map((opt) => {
                    const meta = STYLES.find(s => s.key === opt.style) || { label: opt.style, hint: '' };
                    return (
                      <button
                        key={opt.style}
                        onClick={() => pickBatchOption(opt)}
                        className="w-full text-left rounded-2xl border border-gray-200 hover:border-gray-400 transition-colors p-4 active:scale-[0.99]"
                        data-testid={`schedule-sheet-batch-${opt.style}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className="text-xs font-semibold tracking-tight text-black"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            {meta.label}
                          </span>
                          <Check className="h-3.5 w-3.5 text-gray-300" />
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-3 leading-snug">{opt.caption}</p>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setBatchOptions([])}
                  className="mt-3 text-xs text-gray-500 hover:text-black transition-colors"
                  data-testid="schedule-sheet-batch-back"
                >
                  ← Back to editor
                </button>
              </>
            )}
          </section>

          {/* When section */}
          <section>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3">When</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={fmtDateInput(new Date())}
                className="h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black/40"
                data-testid="schedule-sheet-date"
              />
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black/40"
                data-testid="schedule-sheet-time"
              />
            </div>
          </section>

          {/* CTA */}
          <button
            onClick={handleSchedule}
            disabled={parentLoading}
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B0C10] text-white text-sm font-medium hover:bg-gray-900 active:scale-[0.98] disabled:opacity-60 transition-all"
            data-testid="schedule-sheet-submit"
          >
            {parentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
            Schedule post
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ScheduleSheet;
